import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Loader2,
  Package,
  Pencil,
  Trash2,
  X,
} from "lucide-react";
import { AppShell, type AppShellContext } from "@/components/app/AppShell";
import { api } from "@/lib/api";

type Product = {
  id: string;
  asin: string;
  productTitle: string | null;
  brandId: string;
  brandName: string;
  addedAt: string;
  lastScrapedAt: string | null;
  monitoringEnabled: boolean;
  monitoringCadence: string | null;
  reviewCount: number;
  flaggedCount: number;
};

export default function DashboardProducts() {
  return <AppShell>{(ctx) => <Inner ctx={ctx} />}</AppShell>;
}

function Inner({ ctx }: { ctx: AppShellContext }) {
  const brandId = ctx.activeBrandId;
  const activeBrand = ctx.brands.find((b) => b.id === brandId);
  const [products, setProducts] = useState<Product[] | null>(null);
  const [reload, setReload] = useState(0);
  const [productToEdit, setProductToEdit] = useState<Product | null>(null);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);

  useEffect(() => {
    let cancelled = false;
    setProducts(null);
    api<{ products: Product[] }>("/customer/products").then((res) => {
      if (cancelled) return;
      setProducts(res.data?.products ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [reload]);

  // While the active brand is still discovering products from its storefront,
  // poll the products list every 5s so newly-found ASINs show up without a
  // manual refresh. AppShell separately re-polls /customer/brands to advance
  // the discovery status itself.
  useEffect(() => {
    if (activeBrand?.storefrontDiscoveryStatus !== "discovering") return;
    const id = setInterval(() => setReload((n) => n + 1), 5000);
    return () => clearInterval(id);
  }, [activeBrand?.storefrontDiscoveryStatus]);

  const filtered = useMemo(() => {
    if (!products) return null;
    if (!brandId) return products;
    return products.filter((p) => p.brandId === brandId);
  }, [products, brandId]);

  if (ctx.brands.length === 0) {
    return (
      <div className="mx-auto w-full max-w-3xl px-6 py-16 text-center">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
          No products yet
        </h1>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          Add a brand first — products live under brands.
        </p>
        <Link
          to="/dashboard/brands/new"
          className="mt-6 inline-flex rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
        >
          + Add a brand
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl px-6 py-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
            Products
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {activeBrand
              ? `Every ASIN we monitor for ${activeBrand.name}.`
              : "Every ASIN we monitor."}
          </p>
        </div>
      </div>

      {activeBrand?.storefrontDiscoveryStatus === "discovering" && (
        <div className="mt-6 flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
          <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
          Discovering your products from {activeBrand.name}'s Amazon storefront —
          this usually takes under a minute.
        </div>
      )}

      {filtered === null && (
        <div className="mt-8 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {filtered &&
        filtered.length === 0 &&
        activeBrand?.storefrontDiscoveryStatus !== "discovering" && (
          <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-10 text-center dark:border-slate-800 dark:bg-slate-900">
            <Package className="mx-auto h-8 w-8 text-slate-400" />
            <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
              {activeBrand?.storefrontDiscoveryStatus === "failed"
                ? "We couldn't find any products on your storefront yet."
                : "No products yet on this brand."}
            </p>
          </div>
        )}

      {filtered && filtered.length > 0 && (
        <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
              <tr>
                <th className="px-4 py-2.5 text-left">ASIN</th>
                <th className="px-4 py-2.5 text-left">Brand</th>
                <th className="px-4 py-2.5 text-left">Last scraped</th>
                <th className="px-4 py-2.5 text-left">Reviews</th>
                <th className="px-4 py-2.5 text-left">Flagged</th>
                <th className="px-4 py-2.5 text-right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {filtered.map((p) => (
                <tr
                  key={p.id}
                  className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60"
                  onClick={() => {
                    window.location.href = `/dashboard/products/${p.asin}`;
                  }}
                >
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={`/dashboard/products/${p.asin}`}
                        onClick={(e) => e.stopPropagation()}
                        className="font-mono text-xs text-slate-700 hover:underline dark:text-slate-300"
                      >
                        {p.asin}
                      </Link>
                      {p.monitoringEnabled && (
                        <span className="inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900">
                          Monitored
                        </span>
                      )}
                    </div>
                    {p.productTitle && (
                      <div className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                        {p.productTitle}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                    {p.brandName}
                  </td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400">
                    {p.lastScrapedAt
                      ? new Date(p.lastScrapedAt).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                    {p.reviewCount}
                  </td>
                  <td className="px-4 py-3">
                    {p.flaggedCount > 0 ? (
                      <span className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900">
                        {p.flaggedCount}
                      </span>
                    ) : (
                      <span className="text-slate-500 dark:text-slate-400">0</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setProductToEdit(p);
                        }}
                        aria-label={`Edit ${p.asin}`}
                        className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setProductToDelete(p);
                        }}
                        aria-label={`Remove ${p.asin}`}
                        className="rounded-full p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40 dark:hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {productToEdit && (
        <EditProductModal
          product={productToEdit}
          brands={ctx.brands}
          onCancel={() => setProductToEdit(null)}
          onSaved={() => {
            setProductToEdit(null);
            setReload((n) => n + 1);
          }}
        />
      )}

      {productToDelete && (
        <DeleteProductModal
          product={productToDelete}
          onCancel={() => setProductToDelete(null)}
          onDeleted={() => {
            setProductToDelete(null);
            setReload((n) => n + 1);
          }}
        />
      )}
    </div>
  );
}

function EditProductModal({
  product,
  brands,
  onCancel,
  onSaved,
}: {
  product: Product;
  brands: { id: string; name: string }[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [brandId, setBrandId] = useState(product.brandId);
  const [asin, setAsin] = useState(product.asin);
  const [productTitle, setProductTitle] = useState(product.productTitle ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const res = await api(`/customer/products/${product.id}`, {
      method: "PATCH",
      body: JSON.stringify({
        brandId,
        asin,
        productTitle: productTitle.trim() || null,
      }),
    });
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error ?? "Couldn't update the product.");
      return;
    }
    onSaved();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Edit product
          </h2>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-full p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="mt-5 space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Brand
            </label>
            <select
              value={brandId}
              onChange={(e) => setBrandId(e.target.value)}
              required
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            >
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
              ASIN or Amazon product URL
            </label>
            <input
              required
              value={asin}
              onChange={(e) => setAsin(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 font-mono text-sm text-slate-900 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Product title
            </label>
            <input
              value={productTitle}
              onChange={(e) => setProductTitle(e.target.value)}
              placeholder="Optional"
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            />
          </div>

          {error && (
            <p className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
              <AlertTriangle className="h-4 w-4" /> {error}
            </p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="rounded-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !brandId || !asin.trim()}
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteProductModal({
  product,
  onCancel,
  onDeleted,
}: {
  product: Product;
  onCancel: () => void;
  onDeleted: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    setError(null);
    setSubmitting(true);
    const res = await api(`/customer/products/${product.id}`, {
      method: "DELETE",
    });
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error ?? "Couldn't remove the product.");
      return;
    }
    onDeleted();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Remove {product.asin}?
          </h2>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-full p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
          We&apos;ll stop scraping reviews for this product. Existing flagged
          reviews and takedown history are preserved as audit records.
        </p>
        {error && (
          <p className="mt-4 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
            <AlertTriangle className="h-4 w-4" /> {error}
          </p>
        )}
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="rounded-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={submitting}
            className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitting ? "Removing…" : "Remove"}
          </button>
        </div>
      </div>
    </div>
  );
}
